'use client';

import { Box, Chip, IconButton } from '@mui/material';
import {
  DataGridPro,
  GridColDef,
  GridColumnVisibilityModel,
  GridRenderCellParams,
  GridSortModel,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import { useState, useMemo, useEffect, useCallback } from 'react';
import { Download, Edit as EditIcon } from '@mui/icons-material';
import { ReaxffGridToolbar } from './ReaxffGridToolbar';
import { splitCompoundIntoElements } from './reaxffUtils';
import { dataGridStyle } from '@/styles/tableStyles';
import {
  FileParameter,
  SimulationDataSetDto,
  SimulationDataSetUpdateRequest,
  FileResponse,
  PrivilegeEnum,
} from '@/api/ApiClient';
import { useSystemConfig } from '@/hooks/useSystemConfig';
import { useBackend } from '@/hooks/useBackend';
import { useSnackbar } from '@/hooks/useSnackbar';
import { useAuthStore } from '@/stores/authStore';
import { log } from '@/lib/utils/logger';
import { getProblemDetail } from '@/lib/utils/apiErrors';
import { stripDoiPrefix, toDoiHref } from '@/lib/utils/doiLinks';

import { ReaxffEditDialog } from '@/components/dialog/ReaxffEditDialog';
import { ReaxffUploadDialog } from '@/components/dialog/ReaxffUploadDialog';
import { ReaxffDownloadDialog } from '@/components/dialog/ReaxffDownloadDialog';
import {
  ReaxffSearchPanel,
  defaultReaxffAppliedFilters,
  type ReaxffAppliedFilters,
} from '@/components/tables/search/ReaxffSearchPanel';

/**
 * ReaxFF force-field browser: search panel (Apply/Reset filters) and grid with column/density toolbar.
 */
export default function ReaxFFView() {
  const apiRef = useGridApiRef();
  const config = useSystemConfig();
  const backend = useBackend();
  const { showSnackbar } = useSnackbar();
  const { isAuthorized, currentUser } = useAuthStore();
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>({
    mainAuthor: true,
    elements: true,
    year: true,
    doi: true,
    filename: true,
  });
  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'year', sort: 'desc' }]);

  const [appliedFilters, setAppliedFilters] = useState<ReaxffAppliedFilters>(defaultReaxffAppliedFilters);

  const [editingItem, setEditingItem] = useState<SimulationDataSetDto | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [data, setData] = useState<SimulationDataSetDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [downloadDialogOpen, setDownloadDialogOpen] = useState(false);
  const [selectedDataSet, setSelectedDataSet] = useState<SimulationDataSetDto | null>(null);
  const [downloadId, setDownloadId] = useState<string | null>(null);

  const canUpload = useMemo(() => isAuthorized(PrivilegeEnum.SimulationDataSet, 'create'), [isAuthorized]);
  const canEdit = useMemo(() => isAuthorized(PrivilegeEnum.SimulationDataSet, 'update'), [isAuthorized]);
  const canViewUnpublished = useMemo(() => isAuthorized(PrivilegeEnum.AccessUnpublishedData, 'read'), [isAuthorized]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const result = await backend.simulationDataset.list('ReaxFF');
        setData(result ?? []);
      } catch (error) {
        log.error('[ReaxFFView] Error fetching ReaxFF datasets:', error);
        showSnackbar('Failed to fetch ReaxFF datasets.', 'error');
        setData([]);
      } finally {
        setLoading(false);
      }
    };
    if (currentUser) {
      fetchData();
    }
  }, [backend, currentUser, showSnackbar]);

  const filteredData = useMemo(() => {
    let filtered = data;

    if (!appliedFilters.showUnpublished) {
      filtered = filtered.filter((item) => !!item.doi);
    }

    if (appliedFilters.selectedElements.length > 0) {
      filtered = filtered.filter((item) => {
        const itemElements = item.elements.split(',').flatMap((compound) => splitCompoundIntoElements(compound.trim()));
        return appliedFilters.selectedElements.every((el) => itemElements.includes(el));
      });
    }

    if (appliedFilters.year !== null) {
      filtered = filtered.filter((item) => item.year === appliedFilters.year);
    }

    if (appliedFilters.author !== null) {
      filtered = filtered.filter((item) =>
        item.mainAuthor?.toLowerCase().includes(appliedFilters.author!.toLowerCase())
      );
    }

    return filtered;
  }, [data, appliedFilters]);

  const yearOptions = useMemo(() => {
    const years = new Set(data.map((item) => item.year));
    return Array.from(years).sort((a, b) => b - a);
  }, [data]);

  const authorOptions = useMemo(() => {
    const authors = new Set(data.map((item) => item.mainAuthor).filter(Boolean));
    return Array.from(authors).sort() as string[];
  }, [data]);

  const handleEdit = (item: SimulationDataSetDto) => {
    setEditingItem(item);
    setIsEditModalOpen(true);
  };

  const handleSave = async (update: SimulationDataSetUpdateRequest) => {
    if (!update) return;

    if (!canEdit) {
      showSnackbar('You do not have permission to update ReaxFF datasets', 'error');
      return;
    }

    try {
      await backend.simulationDataset.update(update.id!, update);
      showSnackbar('Successfully updated metadata', 'success');
      setIsEditModalOpen(false);
      const result = await backend.simulationDataset.list('ReaxFF');
      setData(result);
    } catch (error) {
      showSnackbar('Failed to update metadata', 'error');
      log.error('[ReaxFFView] Error updating metadata:', error);
    }
  };

  const handleUpload = () => {
    setIsUploadModalOpen(true);
  };

  const handleUploadSubmit = async (selectedFile: File, metaData: SimulationDataSetDto) => {
    if (!canUpload) {
      showSnackbar('You do not have permission to create ReaxFF datasets', 'error');
      return;
    }

    try {
      const fileParameter: FileParameter = {
        data: selectedFile,
        fileName: selectedFile.name,
      };

      await backend.simulationDataset.uploadFile(fileParameter, JSON.stringify(metaData));
      showSnackbar('Successfully uploaded file', 'success');
      setIsUploadModalOpen(false);
      const result = await backend.simulationDataset.list('ReaxFF');
      setData(result);
    } catch (error) {
      showSnackbar('Failed to upload file', 'error');
      log.error('[ReaxFFView] Error uploading file:', error);
    }
  };

  const downloadFileFromResponse = (response: FileResponse, fileId: number) => {
    const filename = response.fileName || `reaxff_${fileId}.ff`;
    const url = window.URL.createObjectURL(response.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
    showSnackbar('Download started', 'success');
  };

  const handleDownload = async (dataset: SimulationDataSetDto) => {
    if (!config?.apiEndpoint) {
      log.error('[ReaxFFView] API endpoint not configured');
      showSnackbar('System configuration error', 'error');
      return;
    }

    try {
      if (!currentUser?.isAnonymous) {
        log.warn('[ReaxFFView] User is authenticated, why are we even sent here ?!');
        const response = await backend.simulationDataset.getFile(dataset.id!, null);
        downloadFileFromResponse(response, dataset.id!);
        return;
      }

      if (!downloadId) {
        log.info('[ReaxFFView] User is not authenticated, showing download dialog');
        setSelectedDataSet(dataset);
        setDownloadDialogOpen(true);
        return;
      }

      log.info('[ReaxFFView] Using download ID for unauthenticated user:', downloadId);
      const response = await backend.simulationDataset.getFile(dataset.id!, downloadId);
      downloadFileFromResponse(response, dataset.id!);
    } catch (error: any) {
      const errorMessage = getProblemDetail(error) ?? 'Unknown error';
      const errorStatus = error?.status;

      if (errorStatus === 401) {
        setDownloadId(null);
        setSelectedDataSet(dataset);
        setDownloadDialogOpen(true);
        showSnackbar('Please provide your information to download', 'info');
      } else if (errorStatus === 404) {
        showSnackbar('File not found', 'error');
      } else if (errorMessage.includes('Failed to fetch') || errorMessage.includes('Network Error')) {
        showSnackbar('Network error - please check your connection', 'error');
      } else {
        showSnackbar('Failed to start download', 'error');
      }
    }
  };

  const handleDownloadComplete = async (userId: string) => {
    if (!config?.apiEndpoint) {
      log.error('[ReaxFFView] API endpoint not configured');
      showSnackbar('System configuration error', 'error');
      return;
    }

    log.info('[ReaxFFView] Download dialog completed, received userId:', userId);
    setDownloadId(userId);

    if (selectedDataSet !== null) {
      const response = await backend.simulationDataset.getFile(selectedDataSet.id!, userId);
      downloadFileFromResponse(response, selectedDataSet.id!);
    }
  };

  const handleExportCsv = useCallback(() => {
    apiRef.current?.exportDataAsCsv({
      fileName: 'reaxff-force-fields',
      delimiter: ',',
      utf8WithBom: true,
    });
  }, [apiRef]);

  const columns: GridColDef[] = [
    {
      field: 'mainAuthor',
      headerName: 'Author',
      flex: 1,
      minWidth: 150,
    },
    {
      field: 'elements',
      headerName: 'Elements',
      flex: 1,
      editable: true,
      renderCell: (params: GridRenderCellParams<SimulationDataSetDto>) => {
        const elements = params.row.elements.split(',').map((e) => e.trim());
        return (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {elements.map((element, index) => (
              <Chip key={index} label={element} size="small" />
            ))}
          </Box>
        );
      },
    },
    {
      field: 'year',
      headerName: 'Year',
      flex: 0.5,
      minWidth: 100,
    },
    {
      field: 'doi',
      headerName: 'DOI',
      flex: 1,
      minWidth: 200,
      renderCell: (params: GridRenderCellParams<SimulationDataSetDto, string>) =>
        params.value ? (
          <a
            href={toDoiHref(params.value)}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: 'inherit', textDecoration: 'none' }}
          >
            {stripDoiPrefix(params.value)}
          </a>
        ) : null,
    },
    {
      field: 'filename',
      headerName: 'File',
      flex: 1,
      minWidth: 150,
      renderCell: (params: GridRenderCellParams<SimulationDataSetDto, string>) =>
        params.value ? (
          !currentUser?.isAnonymous ? (
            <a
              href={`${config?.apiEndpoint}/api/v1/simulation-dataset/files/${params.row.id}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: 'inherit', textDecoration: 'none' }}
            >
              <Download />
            </a>
          ) : (
            <IconButton
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleDownload(params.row);
              }}
              size="small"
            >
              <Download />
            </IconButton>
          )
        ) : null,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 0.5,
      minWidth: 100,
      renderCell: (params: GridRenderCellParams<SimulationDataSetDto>) => {
        return canEdit ? (
          <IconButton onClick={() => handleEdit(params.row)} size="small">
            <EditIcon />
          </IconButton>
        ) : null;
      },
    },
  ];

  return (
    <>
      <ReaxffSearchPanel
        appliedFilters={appliedFilters}
        onApply={setAppliedFilters}
        onReset={() => setAppliedFilters(defaultReaxffAppliedFilters())}
        yearOptions={yearOptions}
        authorOptions={authorOptions}
        data={data}
        canUpload={canUpload}
        canViewUnpublished={canViewUnpublished}
        onUpload={handleUpload}
        onExportCsv={handleExportCsv}
      />

      <Box sx={{ width: '100%', flex: 1, minHeight: 400, mt: 1 }}>
        <DataGridPro
          apiRef={apiRef}
          rows={filteredData}
          columns={columns}
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={(newModel) => setColumnVisibilityModel(newModel)}
          sortModel={sortModel}
          onSortModelChange={(model) => setSortModel(model)}
          pagination
          pageSizeOptions={[25, 50, 100]}
          disableRowSelectionOnClick
          disableColumnMenu
          loading={loading}
          slots={{
            toolbar: ReaxffGridToolbar,
          }}
          getRowHeight={() => 'auto'}
          sx={dataGridStyle}
          showToolbar
        />
      </Box>

      {editingItem ? (
        <ReaxffEditDialog
          editingItem={editingItem}
          isEditModalOpen={isEditModalOpen}
          setIsEditModalOpen={setIsEditModalOpen}
          handleSave={handleSave}
        />
      ) : null}
      <ReaxffUploadDialog
        isUploadModalOpen={isUploadModalOpen}
        setIsUploadModalOpen={setIsUploadModalOpen}
        handleUploadSubmit={handleUploadSubmit}
      />
      {selectedDataSet ? (
        <ReaxffDownloadDialog
          dataset={selectedDataSet}
          open={downloadDialogOpen}
          onClose={() => setDownloadDialogOpen(false)}
          onDownloadComplete={handleDownloadComplete}
        />
      ) : null}
    </>
  );
}
